import React from "react";
import RhymeGroupsDisplay from "../Shared/RhymeGroupsDisplay";

const StatsTab = ({
  songs,
  stats,
  selectedStatsFilter,
  setSelectedStatsFilter,
  onSearchInLyrics,
}) => {
  return (
    <div className="space-y-6">
      {/* Song Selection Dropdown */}
      <div className="rounded-lg border p-4 transition-colors bg-forest-800 border-forest-600">
        <label className="block text-sm font-medium mb-2 text-forest-200">
          Analyze Statistics For:
        </label>
        <select
          value={selectedStatsFilter}
          onChange={(e) => setSelectedStatsFilter(e.target.value)}
          className="w-full max-w-md p-2 rounded border bg-forest-700 border-forest-600 text-forest-100"
        >
          <option value="all">All Songs ({songs.length} songs)</option>
          {songs.map((song) => (
            <option key={song.id} value={song.id.toString()}>
              {song.title}
            </option>
          ))}
        </select>
      </div>

      {/* Basic Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border p-4 text-center transition-colors bg-forest-800 border-forest-600">
          <div className="text-2xl font-semibold text-forest-100">
            {stats.totalSongs}
          </div>
          <div className="text-sm text-forest-300">
            {selectedStatsFilter === "all" ? "Total Songs" : "Selected Song"}
          </div>
        </div>
        <div className="rounded-lg border p-4 text-center transition-colors bg-forest-800 border-forest-600">
          <div className="text-2xl font-semibold text-forest-100">
            {stats.totalWords.toLocaleString()}
          </div>
          <div className="text-sm text-forest-300">Total Words</div>
        </div>
        <div className="rounded-lg border p-4 text-center transition-colors bg-forest-800 border-forest-600">
          <div className="text-2xl font-semibold text-forest-100">
            {stats.uniqueWords.toLocaleString()}
          </div>
          <div className="text-sm text-forest-300">Unique Words</div>
        </div>
        <div className="rounded-lg border p-4 text-center transition-colors bg-forest-800 border-forest-600">
          <div className="text-2xl font-semibold text-forest-100">
            {stats.totalWords > 0
              ? ((stats.uniqueWords / stats.totalWords) * 100).toFixed(1)
              : 0}
            %
          </div>
          <div className="text-sm text-forest-300">Vocabulary Richness</div>
        </div>
      </div>

      {/* Structure & Composition Stats */}
      <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
        <h3 className="text-lg font-medium mb-4 text-forest-100">
          Structure & Composition
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-xl font-semibold text-forest-100">
              {stats.averageLinesPerSong}
            </div>
            <div className="text-sm text-forest-300">Avg Lines Per Song</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-forest-100">
              {stats.averageWordsPerSong}
            </div>
            <div className="text-sm text-forest-300">Avg Words Per Song</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-forest-100">
              {stats.averageWordLength}
            </div>
            <div className="text-sm text-forest-300">Avg Word Length</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-forest-100">
              {stats.averageSyllablesPerWord}
            </div>
            <div className="text-sm text-forest-300">
              Avg Syllables Per Word
            </div>
          </div>
        </div>
      </div>

      {/* Syllable Distribution */}
      <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
        <h3 className="text-lg font-medium mb-4 text-forest-100">
          Syllable Distribution
        </h3>
        <div className="space-y-3">
          {Object.entries(stats.syllableDistribution)
            .sort(([a], [b]) => {
              if (a === "5+") return 1;
              if (b === "5+") return -1;
              return parseInt(a) - parseInt(b);
            })
            .map(([syllables, count]) => {
              const percentage =
                stats.totalWords > 0
                  ? ((count / stats.totalWords) * 100).toFixed(1)
                  : 0;
              return (
                <div
                  key={syllables}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center w-1/3">
                    <span className="text-sm font-medium text-forest-200">
                      {syllables} syllable{syllables !== "1" ? "s" : ""}
                    </span>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-2 rounded-full bg-forest-700">
                      <div
                        className="h-2 rounded-full bg-gold-500"
                        style={{ width: `${Math.min(100, percentage * 2)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-sm w-20 text-right text-forest-300">
                    {count} ({percentage}%)
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Word Length Distribution */}
      <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
        <h3 className="text-lg font-medium mb-4 text-forest-100">
          Word Length Distribution
        </h3>
        <div className="space-y-3">
          {Object.entries(stats.wordLengthDistribution)
            .sort(([a], [b]) => {
              if (a === "11+") return 1;
              if (b === "11+") return -1;
              return parseInt(a) - parseInt(b);
            })
            .map(([length, count]) => {
              const percentage =
                stats.totalWords > 0
                  ? ((count / stats.totalWords) * 100).toFixed(1)
                  : 0;
              return (
                <div key={length} className="flex items-center justify-between">
                  <div className="flex items-center w-1/3">
                    <span className="text-sm font-medium text-forest-200">
                      {length} character{length !== "1" ? "s" : ""}
                    </span>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-2 rounded-full bg-forest-700">
                      <div
                        className="h-2 rounded-full bg-leaf-500"
                        style={{ width: `${Math.min(100, percentage * 2)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-sm w-20 text-right text-forest-300">
                    {count} ({percentage}%)
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Reading Level & Complexity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
          <h3 className="text-lg font-medium mb-4 text-forest-100">
            Reading Level
          </h3>
          <div className="text-center">
            <div className="text-3xl font-bold mb-2 text-forest-100">
              {stats.readingLevel.toFixed(1)}
            </div>
            <div className="text-sm text-forest-300">
              Flesch-Kincaid Grade Level
            </div>
            <div className="text-xs mt-2 text-forest-300">
              {stats.readingLevel < 6
                ? "Elementary"
                : stats.readingLevel < 9
                  ? "Middle School"
                  : stats.readingLevel < 13
                    ? "High School"
                    : stats.readingLevel < 16
                      ? "College"
                      : "Graduate"}
            </div>
          </div>
        </div>

        <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
          <h3 className="text-lg font-medium mb-4 text-forest-100">
            Vocabulary Complexity
          </h3>
          <div className="text-center">
            <div className="text-3xl font-bold mb-2 text-forest-100">
              {stats.vocabularyComplexity.toFixed(1)}
            </div>
            <div className="text-sm text-forest-300">Complexity Score</div>
            <div className="text-xs mt-2 text-forest-300">
              {stats.vocabularyComplexity < 2
                ? "Simple"
                : stats.vocabularyComplexity < 3
                  ? "Moderate"
                  : stats.vocabularyComplexity < 4
                    ? "Complex"
                    : "Very Complex"}
            </div>
          </div>
        </div>
      </div>

      {/* Rhyme Analysis */}
      <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
        <h3 className="text-lg font-medium mb-4 text-forest-100">
          Rhyme Analysis
        </h3>

        {/* Rhyme Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gold-500">
              {stats.rhymeStats.perfectRhymes}
            </div>
            <div className="text-xs text-forest-300">Perfect Rhymes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-leaf-500">
              {stats.rhymeStats.nearRhymes}
            </div>
            <div className="text-xs text-forest-300">Near Rhymes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-forest-200">
              {stats.rhymeStats.soundsLike}
            </div>
            <div className="text-xs text-forest-300">Sounds Like</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gold-400">
              {stats.rhymeStats.internalRhymes}
            </div>
            <div className="text-xs text-forest-300">Internal Rhymes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-ember-500">
              {stats.rhymeStats.rhymeDensity.toFixed(1)}%
            </div>
            <div className="text-xs text-forest-300">Rhyme Density</div>
          </div>
        </div>

        {/* Top Rhyme Groups */}
        {stats.rhymeStats.allRhymeGroups.length > 0 && (
          <div>
            <h4 className="text-md font-medium mb-3 text-forest-200">
              Top Rhyme Groups
            </h4>
            <RhymeGroupsDisplay
              rhymeGroups={stats.rhymeStats.allRhymeGroups}
              onWordClick={(word) => {
                onSearchInLyrics(word);
              }}
            />
          </div>
        )}
      </div>

      {/* Most Used Words - Enhanced */}
      {stats.mostUsedWords.length > 0 && (
        <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
          <h3 className="text-lg font-medium mb-4 text-forest-100">
            Most Used Words
          </h3>
          <div className="space-y-2">
            {stats.mostUsedWords.map(([word, count], index) => {
              const percentage =
                stats.totalWords > 0
                  ? ((count / stats.totalWords) * 100).toFixed(2)
                  : 0;
              return (
                <div key={word} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm w-6 text-forest-300">
                      {index + 1}.
                    </span>
                    <button
                      onClick={() => onSearchInLyrics(word)}
                      className="px-2 py-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200"
                    >
                      {word}
                    </button>
                  </div>
                  <div className="text-sm text-forest-300">
                    {count} times ({percentage}%)
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsTab;
